import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { ApiError, authPost, mutate, query, sessionState } from '../api.ts';
import { isStandalone, needsInstallFirst, pushSupported } from '../device.ts';
import { enablePushFromTap, refreshPushSubscription } from '../push.ts';

// phase 0 screen: proves login, install and push on a real phone before any
// planner UI exists. it is replaced by the agenda in phase 3.

function InstallGuide() {
  return (
    <section className="card">
      <h2>Install first</h2>
      <p>
        On iPhone, notifications and long-lived storage only work in the installed app. Install it,
        then sign in from the home screen icon.
      </p>
      <ol>
        <li>Tap the Share button in Safari.</li>
        <li>Choose "Add to Home Screen", then "Add".</li>
        <li>Open p14nner from the home screen.</li>
      </ol>
    </section>
  );
}

function BuildInfo() {
  const health = useQuery({
    queryKey: ['healthz'],
    queryFn: async () => (await fetch('/healthz')).json() as Promise<{ build: string }>,
  });
  return (
    <p className="muted small">
      app {__BUILD_SHA__.slice(0, 7)} · server {health.data?.build.slice(0, 7) ?? '…'} ·{' '}
      {isStandalone() ? 'installed' : 'browser tab'}
    </p>
  );
}

function SignIn() {
  const qc = useQueryClient();
  const devLogin = useMutation({
    mutationFn: () => authPost('/auth/dev-login'),
    onSuccess: () => qc.invalidateQueries(),
  });
  const loginError = new URLSearchParams(window.location.search).get('login_error');
  return (
    <section className="card">
      <h2>Sign in</h2>
      {loginError && <p className="error">Sign-in failed: {loginError}</p>}
      <a className="button primary" href="/auth/login?return=/">
        Sign in with n450s
      </a>
      {import.meta.env.DEV && (
        <button type="button" onClick={() => devLogin.mutate()} disabled={devLogin.isPending}>
          Dev sign in
        </button>
      )}
    </section>
  );
}

function PushCard() {
  const key = useQuery({ queryKey: ['push.publicKey'], queryFn: () => query('push.publicKey') });
  const [permission, setPermission] = useState(
    pushSupported() ? Notification.permission : 'unsupported',
  );
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    if (key.data) {
      refreshPushSubscription(key.data.publicKey).catch(() => undefined);
    }
  }, [key.data]);

  const test = useMutation({
    mutationFn: (delaySeconds: number) => mutate('push.test', { delaySeconds }),
    onSuccess: (r, delay) =>
      setStatus(
        delay
          ? `Queued for ${r.queued} device(s). Lock the phone now.`
          : `Sent ${r.sent}, failed ${r.failed}.`,
      ),
    onError: (e) => setStatus(e instanceof ApiError ? e.message : 'request failed'),
  });

  if (permission === 'unsupported') {
    return (
      <section className="card">
        <h2>Notifications</h2>
        <p>
          This browser can't receive web push
          {needsInstallFirst() ? ' until the app is installed' : ''}.
        </p>
      </section>
    );
  }

  return (
    <section className="card">
      <h2>Notifications</h2>
      <p className="muted">Permission: {permission}</p>
      {key.error && <p className="error">{(key.error as Error).message}</p>}
      {permission === 'denied' && (
        <p>
          Notifications are blocked for this app. Allow them in the system settings for p14nner,
          then reopen it.
        </p>
      )}
      {permission === 'default' && key.data && (
        <button
          type="button"
          className="primary"
          onClick={() => {
            // no await before this call: iOS needs the tap to still be live
            enablePushFromTap(key.data.publicKey)
              .then((p) => setPermission(p))
              .catch((e: Error) => setStatus(e.message));
          }}
        >
          Turn on notifications
        </button>
      )}
      {permission === 'granted' && (
        <div className="row">
          <button type="button" onClick={() => test.mutate(0)} disabled={test.isPending}>
            Test now
          </button>
          <button type="button" onClick={() => test.mutate(15)} disabled={test.isPending}>
            Test in 15 s
          </button>
        </div>
      )}
      {status && <p className="muted">{status}</p>}
    </section>
  );
}

function Account() {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ['me'], queryFn: () => query('me') });
  const logout = useMutation({
    mutationFn: () => authPost<{ logoutUrl: string | null }>('/auth/logout'),
    onSuccess: ({ logoutUrl }) => {
      if (logoutUrl) window.location.assign(logoutUrl);
      else void qc.invalidateQueries();
    },
  });
  return (
    <section className="card">
      <h2>{me.data ? me.data.displayName : 'Signed in'}</h2>
      {me.data && <p className="muted">Zone on file: {me.data.timeZone}</p>}
      <button type="button" onClick={() => logout.mutate()} disabled={logout.isPending}>
        Sign out
      </button>
    </section>
  );
}

function Home() {
  const session = useQuery({ queryKey: ['session'], queryFn: sessionState });
  const signedIn = session.data?.authenticated === true;
  return (
    <>
      <h1>Phase 0 checks</h1>
      <BuildInfo />
      {needsInstallFirst() && <InstallGuide />}
      {session.isLoading && <p className="muted">Checking session…</p>}
      {session.isError && (
        <p className="muted">Can't reach the server. The shell still works offline.</p>
      )}
      {session.data && !signedIn && <SignIn />}
      {signedIn && (
        <>
          <Account />
          <PushCard />
        </>
      )}
    </>
  );
}

export const Route = createFileRoute('/')({ component: Home });
